import { useContext, useEffect, useState, useRef, JSX } from "react";
import { DragAndDropFileUploader, PageSection } from "@app/shared";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { BulkTargetingWizardStepProps } from "./interfaces";
import { BulkTargetingWizardContext } from "./TargetingWizardContext";
import { authenticatedDownload } from "../../shared/AuthenticatedDownload";
import { QESAlert, QESButton } from "../../shared/qes-standards";
import { ExportService } from "@app/exports";
import { toast } from "react-toastify";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";


const TargetingWizardImportList = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exportService = new ExportService();
  const helpFileUri = useRef<string>(`${window.apiEndpointV2_0}/projectionproviders/bulktargetingsamplefile/csv`).current;
  const uploadObjectKey = "bulkTargetingFile";
  const [bulkUploadLimit, setBulkUploadLimit] = useState<number>(0);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState<boolean>(true);

  const handleFileUpload = async (data: FormData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await wizardContext.projectionProviderService.importBulkTargetFile(data);

        if (featureFlags.has(Features.BULK_UPLOAD_LIMIT)) {
          if (wizardContext.uploadedTargetCount === 0 || response.uploadLimitationStatus < 0) {
            toast(getUploadLimitMessage(response.uploadLimitationStatus),
              {
                containerId: "z-index-9999",
                type: "error"
              });

            setIsUploadSuccessful(false);
            reject();
          } else {
            setIsUploadSuccessful(true);
            wizardContext.setUploadedTargetCount(response.uploadLimitationStatus);
            const validItems = <>{response.recordCount} Valid Records</>;
            wizardContext.setBulkImportFormData(data);
            wizardContext.setBulkProviderTargetId(response.bulkTargetId);
            wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);

            resolve();
          }
        } else {
          const validItems = <>{response.recordCount} Valid Records</>;
          wizardContext.setBulkImportFormData(data);
          wizardContext.setBulkProviderTargetId(response.bulkTargetId);
          wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);

          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  useEffect(() => {
    if (featureFlags.has(Features.BULK_UPLOAD_LIMIT)) {
      if (!wizardContext.uploadedTargetCount) {
        exportService.getVolumeTargetRowCount(0).then((rowCount) => wizardContext.setUploadedTargetCount(rowCount));
      }
      exportService.getVolumeTargetUploadLimit().then((uploadLimit) => setBulkUploadLimit(uploadLimit));
    }
  }, []);

  useEffect(() => {
    props.stepIsValid(!!wizardContext.bulkImportFormData && !!wizardContext.bulkProviderTargetId && isUploadSuccessful);
  }, [wizardContext.bulkImportFormData, wizardContext.bulkProviderTargetId, isUploadSuccessful]);

  const getUploadLimitMessage = (status: number): string => {
    if (wizardContext.uploadedTargetCount === 0 || status < 0) {
      return `Your Volume Targeting limit has been exceeded. Volume targeting is limited to ${bulkUploadLimit} providers per day. Please try again later.`;
    }
  }

  const downloadSampleFile = () => {
    authenticatedDownload(helpFileUri, undefined);
  }

  return (
    <PageSection>
      <div className="container">
        <p className="mt-3">Please upload a <code>.csv</code> file. Find correct formatting and information needed  <QESButton qesType="secondary" className="bg-transparent font-weight-normal p-0" qesTestId="import-targeting__sample-file" onClick={downloadSampleFile}>here</QESButton>.</p>
        {featureFlags.has(Features.BULK_UPLOAD_LIMIT) && <p className="mt-3">{`You have ${wizardContext.uploadedTargetCount} targets left to use for Volume Targeting.`}</p>}
        <QESAlert type="info" classNames="mb-2" persist={true}>
          All specialty codes with leading zeros must have that reflected in the upload file.
        </QESAlert>
        <DragAndDropFileUploader apiKeyName={uploadObjectKey} onUpload={handleFileUpload} filetype=".csv" hasBeenSuccessful={!!wizardContext.bulkImportFormData} testId="import-terminations__file-input" />
      </div>
    </PageSection>
  );
};

export default TargetingWizardImportList;
